import { useCallback, useEffect, useState } from 'react';

import { Api } from '../../api/api';

import type { DashboardState } from './DashboardState';

/** Mirrors android/…/Screens/Dashboard/DashboardViewModel.kt. */
export function useDashboard(childId: string | null) {
  const [state, setState] = useState<DashboardState>({
    isLoading: true,
    dashboard: null,
    errorPatterns: [],
    error: null,
    errorCorrelationId: null,
  });

  const load = useCallback(async (): Promise<void> => {
    if (childId === null) return;

    setState((current) => ({ ...current, isLoading: true, error: null, errorCorrelationId: null }));

    const dashboard = await Api.readDashboard(childId);

    if (!dashboard.ok) {
      setState((current) => ({
        ...current,
        isLoading: false,
        error: dashboard.error.message,
        errorCorrelationId: dashboard.error.correlationId ?? null,
      }));

      return;
    }

    // The patterns are a second call and a second failure mode. A dashboard that renders without them is
    // still the dashboard; refusing to show it because one section failed would be worse.
    const patterns = await Api.readErrorPatterns(childId);

    setState({
      isLoading: false,
      dashboard: dashboard.value,
      errorPatterns: patterns.ok ? patterns.value : [],
      error: null,
      errorCorrelationId: null,
    });
  }, [childId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { state, load };
}
